import React, { useEffect, useState } from 'react';
import { useDebounce, useLogger, useSelector } from '~Hooks';
import { useI18n } from '~Hooks';
import { useLazyQuery, useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import {
  WaiterAidGiftcardBalance,
  WaiterAidGiftcardBalanceVariables,
} from './__queries__/WaiterAidGiftcardBalance';
import {
  WaiterAidRedeemGiftcard,
  WaiterAidRedeemGiftcardVariables,
} from './__queries__/WaiterAidRedeemGiftcard';

import { SwipeView } from '../GiftCard/Components/SwipeView';
import { ManualView } from '../GiftCard/Components/ManualView';
import { QRScanView } from '../GiftCard/Components/QRScanView';
import { Subtitle, Tab, Tabs } from '~Components/Microdeb';

import styled from '@emotion/styled';
import { Box, Button, Card } from '~Styles';
import Loader from '~ComponentsImproved/Loader';
import Modal from '~ComponentsImproved/Modal';
import { handyBreakpoint } from '~Styles/breakpoints';

const WAITERAID_GIFTCARD_BALANCE = gql`
  query WaiterAidGiftcardBalance($giftcardId: String!, $venueId: String!) {
    operativeViewer {
      id
      waiteraidGiftcardBalance(giftcardId: $giftcardId, venueId: $venueId) {
        success
        data
        error
      }
    }
  }
`;

const WAITERAID_REDEEM_GIFTCARD = gql`
  mutation WaiterAidRedeemGiftcard($giftcardId: String!, $amount: Float!, $venueId: String!) {
    waiteraidRedeemGiftcard(giftcardId: $giftcardId, amount: $amount, venueId: $venueId) {
      success
      data
      error
    }
  }
`;

enum TAB_TYPES {
  SWIPE = 'SWIPE',
  MANUAL = 'MANUAL',
  QR = 'QR',
}

type TransactionState = 'READ_CARD' | 'LOADING' | 'DISPLAY_BALANCE' | 'SUCCESS' | 'ERROR';

interface Props {
  id?: string;
  amount: number;
  onComplete: (transactionId?: string) => void;
  onFail: (reason?: string) => void;
}

const ModalBody = styled(Card)`
  background-color: var(--colorBlueGray1300);
  color: var(--colorBlueGray100);
  display: grid;
  justify-items: center;
  align-items: center;
  min-height: 260px;
  min-width: 300px;
  max-width: 600px;
  transition: all 200ms ease-out;
  animation: fade-and-translate-in 200ms cubic-bezier(0.215, 0.61, 0.355, 1);

  ${handyBreakpoint} {
    padding: 24px 0;
    margin-top: 0;
    margin-bottom: auto;
    align-items: flex-start;
  }
`;

const ProgressButton = styled(Button)`
  justify-self: flex-end;
  color: white;
  background-color: var(--colorBlueGray1200);
`;

const WaiterAidGiftcardPayment: React.FunctionComponent<Props> = ({
  id,
  amount,
  onComplete,
  onFail,
}) => {
  const [tabType, setTabType] = useState(TAB_TYPES.SWIPE);
  const [transactionState, setTransactionState] = useState<TransactionState>('READ_CARD');
  const [error, setError] = useState<string | undefined>();
  const [availableBalance, setAvailableBalance] = useState<null | number>(null);
  const [cardNumber, setCardNumber] = useState<string>('');
  const venueId = useSelector((state) => state.venueId!);
  const { i18n } = useI18n();
  const logger = useLogger('waiteraid-giftcard');

  const debouncedCardNumber = useDebounce(cardNumber);

  const [fetchBalance] = useLazyQuery<WaiterAidGiftcardBalance, WaiterAidGiftcardBalanceVariables>(
    WAITERAID_GIFTCARD_BALANCE,
    { fetchPolicy: 'no-cache' },
  );

  const [redeemGiftcard] = useMutation<WaiterAidRedeemGiftcard, WaiterAidRedeemGiftcardVariables>(
    WAITERAID_REDEEM_GIFTCARD,
  );

  // Delays the exit so the success message can be read
  const startDelayedExit = () => {
    setTimeout(() => {
      onComplete(id ? id : undefined);
    }, 1600);
  };

  // When a card number is available, check the balance
  useEffect(() => {
    if (debouncedCardNumber.length <= 0) {
      return;
    }

    setTransactionState('LOADING');

    fetchBalance({
      variables: { giftcardId: debouncedCardNumber, venueId },
    })
      .then(({ data }) => {
        const payload = data?.operativeViewer?.waiteraidGiftcardBalance;

        if (!payload?.success) {
          throw new Error(payload?.error || 'Failed to fetch balance');
        }

        // The balance comes back as a JSON string in the data field
        const balanceData = JSON.parse(payload.data);
        const balance = parseFloat(balanceData.balance ?? balanceData);

        logger.info('Balance fetched', { giftcardId: debouncedCardNumber, balance });

        if (isNaN(balance)) {
          throw new Error('Could not determine balance on card');
        }

        // Balance from WaiterAid is in currency units, convert to cents
        setAvailableBalance(Math.round(balance * 100));
        setTransactionState('DISPLAY_BALANCE');
      })
      .catch((err) => {
        logger.error('Failed to fetch balance', {
          err: err.message,
          giftcardId: debouncedCardNumber,
        });
        setTransactionState('READ_CARD');
        setError(err.message);
      });
  }, [debouncedCardNumber]);

  function executePayment() {
    if (availableBalance! < amount) {
      setTransactionState('ERROR');
      setError('Not enough balance');
      return;
    }

    setTransactionState('LOADING');

    // WaiterAid expects amount in currency units (not cents)
    const amountInCurrency = amount / 100;

    redeemGiftcard({
      variables: {
        giftcardId: debouncedCardNumber,
        amount: amountInCurrency,
        venueId,
      },
    })
      .then(({ data }) => {
        const payload = data?.waiteraidRedeemGiftcard;

        if (!payload?.success) {
          throw new Error(payload?.error || 'Failed to redeem gift card');
        }

        logger.info('WaiterAid giftcard payment successful', {
          giftcardId: debouncedCardNumber,
          amount: amountInCurrency,
        });
        setTransactionState('SUCCESS');
        startDelayedExit();
      })
      .catch((err) => {
        logger.error('WaiterAid giftcard payment failed', {
          giftcardId: debouncedCardNumber,
          amount: amountInCurrency,
          err: err.message,
        });
        setError(err.message);
        setTransactionState('ERROR');
      });
  }

  const handleTouchOutside = () => {
    if (transactionState === 'SUCCESS' || transactionState === 'LOADING') {
      return;
    }
    onFail('Background pressed');
  };

  return (
    <Modal isShowing={true} hide={handleTouchOutside} zIndex={60}>
      <ModalBody padding="large">
        {transactionState === 'READ_CARD' && (
          <>
            <Tabs>
              <Tab
                onClick={() => setTabType(TAB_TYPES.SWIPE)}
                active={tabType === TAB_TYPES.SWIPE}>
                {i18n('GC.SWIPE')}
              </Tab>
              <Tab
                onClick={() => setTabType(TAB_TYPES.MANUAL)}
                active={tabType === TAB_TYPES.MANUAL}>
                {i18n('GC.MANUAL')}
              </Tab>
              <Tab onClick={() => setTabType(TAB_TYPES.QR)} active={tabType === TAB_TYPES.QR}>
                {i18n('GC.QR')}
              </Tab>
            </Tabs>

            {tabType === TAB_TYPES.SWIPE && (
              <SwipeView cardNumber={cardNumber} setCardNumber={setCardNumber} />
            )}
            {tabType === TAB_TYPES.MANUAL && <ManualView submit={setCardNumber} />}
            {tabType === TAB_TYPES.QR && <QRScanView submit={setCardNumber} />}
            {error && <p>{error}</p>}
          </>
        )}
        {transactionState === 'LOADING' && <Loader showDimmer={false} />}
        {transactionState === 'DISPLAY_BALANCE' && availableBalance !== null && (
          <>
            {i18n('GC.CardObtained')}
            <Subtitle>
              {i18n('GC.AvailableBalance')}: {(availableBalance / 100).toFixed(2)}
            </Subtitle>
            <ProgressButton
              fluid
              onClick={(e) => {
                e.preventDefault();
                executePayment();
              }}>
              {i18n('GC.Continue')}
            </ProgressButton>
          </>
        )}
        {transactionState === 'ERROR' && (
          <>
            {i18n('GC.Wrong')}
            <Subtitle>{error}</Subtitle>
            <ProgressButton fluid onClick={() => onFail(error)}>
              {i18n('GC.OK')}
            </ProgressButton>
          </>
        )}
        {transactionState === 'SUCCESS' && <h3>{i18n('General.Success')}</h3>}
      </ModalBody>
    </Modal>
  );
};

export default WaiterAidGiftcardPayment;
